import { useEffect } from 'react';

import { supabaseClient, useUser } from '@auth-v2';
import { useQueryClient } from '@tanstack/react-query';

import { Activity, subscribeToActivity } from '@data';
import { queryKeys } from '@queries';

export const useSubscribeToActivity = (activityId: number) => {
    const { data: user } = useUser();
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!user?.id || activityId < 0) return;
        const subscription = subscribeToActivity(supabaseClient, activityId, async (updatedActivity) => {
            const queryKey = queryKeys.activities.current(user.id);
            // this code facilitates intra session activity change.
            // If we get a finished activity clear it from the cache
            // Scenario, you change to putter, you have id 125 and you are creating 126
            // We have subscriptions to both ids, this prevents that we reload activity 125
            if (updatedActivity.endTime || updatedActivity.deleted) {
                if (queryClient.getQueryData<Activity | null>(queryKey)?.id !== activityId) return;

                await queryClient.cancelQueries({ queryKey });
                queryClient.setQueryData<Activity | null>(queryKey, (activity) =>
                    activity?.id === activityId ? null : activity,
                );
                return;
            }

            queryClient.invalidateQueries({ queryKey });
            queryClient.invalidateQueries({ queryKey: queryKeys.activities.one(activityId) });
        });
        return () => {
            subscription.unsubscribe();
        };
    }, [activityId, queryClient, user?.id]);
};
